import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../__legacy__/models/error.model';
import { IModelImageDto } from '../../../__legacy__/modules/annotation/annotations.interface';
import { IImageAnnotationsResponse } from '../../../__legacy__/modules/annotation/submodules/segmentations/segmentationContextMenu/segmentationsApi.service';
import { IDatasetDetails, IDatasetPreviewImage } from '../../../__legacy__/modules/datasets/datasetsDetails.store';
import { IMainApiService, IPagedResult } from '../../../__legacy__/services/api.service';
import { IApiService } from '../../../__legacy__/services/api.service.base';
import {
  IDatasetImagesDto,
  IJobDatasetDto,
  IJobImageDto,
  IJobProjectDto,
  IProjectDatasetDto,
  IProjectDto,
  IProjectImagesDto,
  IWorkspaceDto,
} from '../jobs.api.model';

export interface IImagesApiService {
  getWorkspaceProjectsAsync: (request: IWorkspaceDto) => Promise<IJobProjectDto[] | ACDLError>;
  getProjectDatasetsAsync: (request: IProjectDto) => Promise<IProjectDatasetDto[] | ACDLError>;
  getProjectImagesAsync: (request: IProjectImagesDto) => Promise<IPagedResult<IJobImageDto> | ACDLError>;
  getDatasetImagesAsync: (request: IDatasetImagesDto) => Promise<IPagedResult<IJobImageDto> | ACDLError>;
  getWorkspaceDatasetsAsync: (request: IWorkspaceDto) => Promise<IJobDatasetDto[] | ACDLError>;
  getDatasetsImagesAsync: (datasetIds: string[]) => Promise<IModelImageDto[] | ACDLError>;
  getDatasetDetailsAsync: (datasetId: string) => Promise<IDatasetDetails | ACDLError>;
  getImageAnnotationsAsync: (imageId: string) => Promise<IImageAnnotationsResponse | ACDLError>;
  getImageDetailsAsync: (datasetId: string, imageId: string) => Promise<IDatasetPreviewImage | ACDLError>;
}

export namespace IImagesApiService {
  export const $: interfaces.ServiceIdentifier<IImagesApiService> = Symbol('JOB_IMAGES_API_SERVICE');
}

@injectable()
export class ImagesApiService implements IImagesApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  getWorkspaceProjectsAsync = async (request: IWorkspaceDto): Promise<IJobProjectDto[] | ACDLError> => {
    return this.apiService.getAsync('/Models/Projects', { params: request });
  };

  getProjectDatasetsAsync = async (request: IProjectDto): Promise<IProjectDatasetDto[] | ACDLError> => {
    return this.apiService.getAsync('/Models/Datasets', { params: request });
  };

  getProjectImagesAsync = async (request: IProjectImagesDto): Promise<IPagedResult<IJobImageDto> | ACDLError> => {
    return this.apiService.postAsync('/Models/Images', request);
  };

  getDatasetImagesAsync = async (request: IDatasetImagesDto): Promise<IPagedResult<IJobImageDto> | ACDLError> => {
    return this.apiService.getAsync('/Datasets/Images', { params: request });
  };

  getWorkspaceDatasetsAsync = async (request: IWorkspaceDto): Promise<IJobDatasetDto[] | ACDLError> => {
    return this.apiService.getAsync('/Models/DatasetsInWorkspace', { params: request });
  };

  getDatasetsImagesAsync = async (datasetsIds: string[]): Promise<IModelImageDto[] | ACDLError> => {
    return this.apiService.getAsync(`/Models/DatasetsImages`, {
      params: {
        datasetsIds,
      },
      paramsSerializer: { indexes: null },
    });
  };

  getDatasetDetailsAsync = async (datasetId: string): Promise<IDatasetDetails | ACDLError> => {
    return this.apiService.getAsync(`/datasets/details`, {
      params: {
        DatasetId: datasetId,
      },
    });
  };

  getImageDetailsAsync = async (datasetId: string, imageId: string): Promise<IDatasetPreviewImage | ACDLError> => {
    return this.apiService.getAsync(`/datasets/${datasetId}/imageInfo/${imageId}`);
  };

  getImageAnnotationsAsync = async (imageId: string): Promise<IImageAnnotationsResponse | ACDLError> => {
    return this.apiService.getAsync(`/Images/PeekImageAnnotations?imageId=${imageId}`);
  };
}
